import { useEffect, useState } from "react";

import * as S from "./styles";

import Close from "assets/close-small.svg";
import Car from "../Cards/OccurrenceCard/assets/car.svg";
import Home from "../Cards/OccurrenceCard/assets/home.svg";
import { IPoliceman } from "interfaces/Policeman";
import { getPolicemanMapService } from "services/PolicemenServices";
import { MapPopupSkeleton } from "./skeleton";
import { fireToast } from "services/sweetAlertConfig";

type Props = {
  policemanCoords: Markers;
  onClose: () => void;
  refreshOnly?: boolean;
};

type Coords = {
  latitude: number;
  longitude: number;
};

type Markers = {
  id: string;
  coords: Coords;
};

type WorkingOccurrence = {
  id: string;
  patrimonialType: number;
};

function MapPopup({ policemanCoords, onClose }: Props) {
  const [available, setAvailable] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [policeman, setPoliceman] = useState<IPoliceman | undefined>();
  const [workingOccurrence, setWorkingOccurrence] = useState<
    WorkingOccurrence | undefined
  >();

  useEffect(() => {
    fetchPoliceman();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (policeman && policemanCoords.id !== policeman.id) {
      setIsLoading(true);
      fetchPoliceman();
    }
  }, [policemanCoords]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchPoliceman() {
    await getPolicemanMapService(policemanCoords.id)
      .then((res: any) => {
        const { policeman, workStatus } = res;

        if (!policeman && !workStatus) {
          onError();
        }
        const data: IPoliceman = {
          active: policeman.active,
          approved: policeman.approved,
          createdAt: policeman.createdAt,
          id: policeman.id,
          occurrencesAttended: policeman.occurrencesAttended,
          occurrencesDenied: policeman.occurrencesDenied,
          publicAgent: policeman.publicAgent,
          region: policeman.region,
          salary: policeman.salary,
          updatedAt: policeman.updatedAt,
          user: policeman.user,
          working: policeman.working,
          sigInAt: policeman.sigInAt,
          pictures: policeman.pictures,
          patrimonialValues: policeman.patrimonialValues,
          vehicleValues: policeman.vehicleValues,
        };

        setPoliceman({ ...data });
        if (workStatus) {
          setAvailable(false);
          setWorkingOccurrence({
            id: workStatus.data.occurrence.id,
            patrimonialType: workStatus.data.occurrence.patrimonialType,
          });
        }

        setIsLoading(false);
      })
      .catch((err) => {
        console.log({ err });
        onError();
      });
  }

  function onError() {
    onClose();
    setPoliceman(undefined);
    fireToast(
      "Não foi possível recuperar as informações desse pronta-resposta",
      4000
    );
  }

  function copyPolicemanId() {
    navigator.clipboard.writeText(policemanCoords.id);
    fireToast("ID do pronta-resposta copiado com sucesso", 2000);
  }

  function copyPolicemanCoords() {
    const coords = policemanCoords.coords;

    navigator.clipboard.writeText(`${coords.latitude}, ${coords.longitude}`);

    fireToast("Coordenadas do pronta-resposta copiadas com sucesso", 2000);
  }

  function renderOccurrenceTypeIcon() {
    return workingOccurrence?.patrimonialType === 1 ? (
      <img
        src={Home}
        alt="Ícone do respectivo tipo de ocorrência: Patrimonial"
      />
    ) : (
      <img src={Car} alt="Ícone do respectivo tipo de ocorrência: Veicular" />
    );
  }

  if (isLoading) {
    return <MapPopupSkeleton />;
  }

  return (
    <S.Wrapper>
      <S.Container>
        <S.Header>
          <header>
            <S.Title>{policeman && policeman.user.name}</S.Title>

            <S.CoordinateText>
              {policemanCoords.coords.latitude},{" "}
              {policemanCoords.coords.longitude}
            </S.CoordinateText>
          </header>

          <S.CloseButton type="button" onClick={() => onClose()}>
            <img src={Close} alt="Botão de fechar o pop-up" />
          </S.CloseButton>
        </S.Header>

        <div>
          <S.ButtonContainer>
            <S.PopupButton onClick={() => copyPolicemanId()}>
              Copiar ID do pronta-resposta
            </S.PopupButton>
            <S.PopupButton onClick={() => copyPolicemanCoords()}>
              Copiar latitude e longitude
            </S.PopupButton>
          </S.ButtonContainer>

          <S.WorkingStatus available={available}>
            <S.Info>
              {!available && renderOccurrenceTypeIcon()}
              <S.Description available={available}>
                <p>{available ? "Disponível" : "Em ocorrência"}</p>
              </S.Description>
            </S.Info>
          </S.WorkingStatus>
        </div>
      </S.Container>
    </S.Wrapper>
  );
}

export { MapPopup };
